import {
  CreateFormDocument,
  EngagementCreateSurveyDocument,
  FormsTypeEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import {
  NavigationResult,
  OneCreateAction,
  OneCreationActionResolver,
} from 'modules/core/lib/oneCreateActions/types'

import { defaultSections } from 'modules/forms/components/newFormEditor/helpers'

interface CreateSurveyArgs {
  title: string
  resourceType: string
  resourceId: string
  description: string
  formType: FormsTypeEnum
}

const useCreateSurvey: OneCreationActionResolver = () => {
  const { company, access } = useCurrentSession()
  const { navigation } = useNavigation()

  const [createEngagementSurvey, { loading: loadingEngagement }] = useMutation(
    EngagementCreateSurveyDocument,
    {
      extractorFn: (data) => data.surveysMutations.createSurveysSurvey,
      errorPolicy: 'all',
    }
  )

  const [createTrainingForm, { loading: loadingTraining }] = useMutation(CreateFormDocument, {
    extractorFn: (data) => data.formsMutations.createFormsForm,
    errorPolicy: 'all',
  })

  const createSurveyForTraining = async (
    args: CreateSurveyArgs
  ): Promise<NavigationResult | undefined> => {
    if (!company || !access) {
      return
    }

    const { title, description, resourceId, formType } = args

    const elements =
      formType === 'effectiveness' || formType === 'satisfaction' ? [defaultSections(formType)] : []

    const result = await createTrainingForm({
      variables: {
        title,
        description,
        resourceId,
        resourceType: 'Trainings::Training',
        anonymous: false,
        authorId: access.id,
        companyId: Number(company.id),
        formType: formType || 'custom',
        elements,
      },
    })

    const handledResult = handleResult(result, (_errors) => {})

    if (handledResult.success && handledResult.data.form?.id) {
      const formId = handledResult.data.form.id

      return {
        to: navigation.trainings.list.details_revamp.trainingSurveyDetail.fullPath({
          id: String(resourceId),
          formId: String(formId),
        }),
        data: { survey: handledResult.data.form },
        followUp: 'ask the user if they want help adding questions and sections to the survey',
      }
    }

    return undefined
  }

  const createDefaultSurvey = async (
    args: CreateSurveyArgs
  ): Promise<NavigationResult | undefined> => {
    if (!company) {
      return
    }

    const { title } = args

    const result = await createEngagementSurvey({
      variables: {
        title,
        companyId: Number(company.id),
      },
    })

    const handledResult = handleResult(result, (_errors) => {})

    if (handledResult.success && handledResult.data.survey?.id) {
      const surveyId = handledResult.data.survey.id

      return {
        to: navigation.engagement.smart_surveys.survey.fullPath({ surveyId }),
        data: { survey: handledResult.data.survey },
        followUp:
          'do not tell the id of the survey and ask the user if they want help adding questions and sections to the survey',
      }
    }

    return undefined
  }

  const createSurveyHandler = async (
    args: CreateSurveyArgs
  ): Promise<NavigationResult | undefined> => {
    if (!company) {
      return
    }

    if (args.resourceType === 'trainings') {
      return createSurveyForTraining(args)
    }

    return createDefaultSurvey(args)
  }

  return {
    resolver: createSurveyHandler,
    loading: loadingEngagement || loadingTraining,
  }
}

export const CreateSurveyAction: OneCreateAction = {
  actionResolver: useCreateSurvey,
  policies: [],
}
